/**
 * Turns a failed transaction into something the user can act on.
 *
 * Two different failure layers land here and they need different treatment:
 *
 *   - the **wallet** rejects or stalls the request (EIP-1193 / JSON-RPC codes).
 *     The user can usually fix these themselves, but only if we say what to do.
 *   - the **contract** reverts with a custom error. The name is the diagnosis;
 *     the user just needs it in plain language.
 *
 * Anything unrecognised keeps viem's `shortMessage` *and* its `details`. An
 * earlier version returned only the first line, which reduced
 * "Requested resource not available. Request of type 'eth_sendTransaction'
 * already pending for origin http://localhost:3000." to its useless half and
 * left people staring at a message with no next step.
 */
import {
  BaseError,
  ChainMismatchError,
  HttpRequestError,
  ResourceUnavailableRpcError,
  TimeoutError,
  UserRejectedRequestError,
} from "viem";

/**
 * Anything that looks like an endpoint. Matches greedily to the next whitespace
 * because a keyed RPC URL carries its secret in the path or query string —
 * `https://rpc.example/v1/<key>` — so the host alone is not the sensitive part.
 */
const URL_PATTERN = /\bhttps?:\/\/\S+/gi;

/**
 * Strips RPC endpoints out of anything that will be sent to a browser.
 *
 * viem puts the full request URL in `error.message`:
 *
 *     HTTP request failed.
 *     URL: https://rpc.example/v1/SECRET-KEY
 *
 * Several API routes hand that message back as `chainError` to help operators
 * debug. Without this, an outage on a keyed endpoint would publish the key to
 * every visitor who happened to load the page during it — and a paid RPC key is
 * exactly the kind of thing that gets quietly resold. The rest of the message
 * is kept: "HTTP request failed. Status: 503" is the useful half and is not
 * secret.
 */
export function redactRpcUrls(message: string): string {
  return message.replace(URL_PATTERN, "[rpc endpoint]");
}

/**
 * Server-side chain read failure, safe to put in a response body.
 * Log the original with `console.error` — server logs are not public.
 */
export function describeChainError(error: unknown): string {
  const raw = error instanceof Error ? error.message : "RPC unavailable";
  return redactRpcUrls(raw);
}

/**
 * Absolute filesystem paths: one or more `/segment` runs that *begin* a token.
 *
 * The leading boundary is captured rather than matched with a lookbehind, both
 * for older Safari and because it is the whole point — without it the pattern
 * happily starts mid-token and turns `2026/08/03` into a redacted path. URLs
 * are redacted before this runs, so `https://host/path` never reaches it.
 */
const ABSOLUTE_PATH_PATTERN = /(^|[\s'"([])((?:\/[^\s'"<>|,]+)+)/g;

/**
 * Database failure, safe to put in a response body.
 *
 * Node names the file it could not open — `EACCES: permission denied, mkdir
 * '/data'` — and the "already open at X, not Y" guard names two. None of that
 * is a credential, but it hands out the container's layout for free, which is
 * worth exactly as much to an attacker as it is worthless to a visitor: the UI
 * shows a fixed sentence and never the raw message. So it is redacted here and
 * kept in full in the server log.
 */
export function describeDatabaseError(error: unknown): string {
  const raw = error instanceof Error ? error.message : "Database unavailable";
  return redactRpcUrls(raw).replace(ABSOLUTE_PATH_PATTERN, (_match, boundary, path) => {
    // Keep sentence-ending punctuation the path swallowed, so the message
    // still reads as prose.
    const trailingDots = /\.+$/.exec(path as string)?.[0] ?? "";
    return `${boundary as string}[database path]${trailingDots}`;
  });
}

/**
 * Turns a failed network switch into something the user can act on.
 *
 * Mostly a mobile problem. `wallet_switchEthereumChain` is well supported by
 * desktop extensions, but several mobile wallets either do not implement it or
 * expect the user to change networks in the app's own settings. Without a
 * message saying so, the "Pindah ke ..." button looks broken and the user has
 * no idea the fix is two taps away inside their wallet.
 */
export function describeSwitchError(
  error: unknown,
  walletName: string,
  networkName: string,
): string {
  const chain = nestedErrors(error);
  const codes = chain.map((item) => item.code).filter((value) => typeof value === "number");
  const names = chain.map((item) => item.name).filter(Boolean);

  // Reown/WalletConnect can wrap an unapproved chain as code 4001 even though
  // the user did not press Cancel. Read the technical cause before the code so
  // Trust Wallet users are not blamed for a session capability mismatch.
  if (isUnsupportedChainRequest(error)) {
    if (!/trust\s*wallet/i.test(walletName)) {
      return (
        `${walletName} tidak mendukung ${networkName} pada koneksi ini. ` +
        `Pilih jaringan tersebut secara manual di wallet, lalu hubungkan ulang ke portal.`
      );
    }

    return (
      `${walletName} tidak mendukung ${networkName} pada sesi WalletConnect ini. ` +
      `Buka portal di DApp Browser ${walletName}, lalu hubungkan wallet langsung dari sana.`
    );
  }
  if (codes.includes(4001)) return "Perpindahan jaringan dibatalkan di wallet.";
  // 4200 "unsupported method" / -32601 "method not found". Both mean the wallet
  // will not do this for us.
  if (codes.includes(4200) || codes.includes(-32601)) {
    return (
      `${walletName} tidak bisa berpindah jaringan dari halaman web. Buka ` +
      `aplikasi wallet, ganti jaringannya ke ${networkName} secara manual, ` +
      `lalu kembali ke halaman ini.`
    );
  }
  if (codes.includes(-32002)) {
    return `${walletName} masih menahan permintaan sebelumnya. Selesaikan permintaan itu lalu coba lagi.`;
  }

  if (names.includes("SwitchChainNotSupportedError")) {
    return (
      `${walletName} tidak mendukung perpindahan jaringan dari portal. Buka ` +
      `aplikasi wallet, ganti jaringannya ke ${networkName} secara manual, ` +
      `lalu kembali ke halaman ini.`
    );
  }

  const raw = chain.map((item) => item.message?.trim()).find(Boolean);
  const detail = raw ? ` Detail wallet: ${redactRpcUrls(raw.split("\n")[0]!)} ` : " ";
  return (
    `Gagal berpindah ke ${networkName}.${detail}` +
    `Ganti jaringan secara manual di ${walletName}, lalu kembali ke portal.`
  );
}

/** Detects WalletConnect's unsupported-chain message through viem/wagmi wrappers. */
export function isUnsupportedChainRequest(error: unknown): boolean {
  return nestedErrors(error).some((item) => {
    const message = item.message?.toLowerCase() ?? "";
    return (
      /request(?:ed)? chains? (?:are|is) not supported/.test(message) ||
      /requested chain(?: id)? .*not supported/.test(message) ||
      /unsupported chain(?: id)?/.test(message)
    );
  });
}

type ErrorNode = { code?: number; message?: string; name?: string };

/** Walks wagmi/viem wrappers until the provider's original RPC error appears. */
function nestedErrors(error: unknown): ErrorNode[] {
  const result: ErrorNode[] = [];
  const queue: unknown[] = [error];
  const seen = new Set<unknown>();

  while (queue.length > 0 && result.length < 12) {
    const current = queue.shift();
    if (!current || (typeof current !== "object" && typeof current !== "function")) continue;
    if (seen.has(current)) continue;
    seen.add(current);

    const node = current as ErrorNode & {
      cause?: unknown;
      data?: { originalError?: unknown };
      error?: unknown;
    };
    result.push(node);
    queue.push(node.cause, node.data?.originalError, node.error);
  }

  return result;
}

/** Custom error name -> what it means for the person reading it. */
const CONTRACT_ERRORS: ReadonlyArray<readonly [string, string]> = [
  // Claim
  ["AlreadyClaimed", "Alokasi untuk alamat ini sudah pernah diklaim."],
  ["InvalidProof", "Bukti klaim tidak cocok dengan snapshot di kontrak."],
  ["ClaimNotStarted", "Periode klaim belum dibuka."],
  ["ClaimWindowClosed", "Periode klaim sudah ditutup."],
  ["AmountIsZero", "Jumlah klaim nol."],
  // Staking
  ["PositionNotMatured", "Posisi belum jatuh tempo."],
  ["EarlyExitNotAllowed", "Plan ini tidak mengizinkan penarikan awal."],
  ["InsufficientRewardPool", "Sisa anggaran reward tidak cukup untuk jumlah ini."],
  ["BelowMinimumStake", "Jumlah di bawah minimum plan ini."],
  ["PlanNotActive", "Plan ini sudah ditutup untuk stake baru."],
  // Shared
  ["EnforcedPause", "Kontrak sedang dijeda oleh operator."],
  ["ERC20InsufficientBalance", "Saldo token tidak cukup."],
  ["ERC20InsufficientAllowance", "Approve belum cukup untuk jumlah ini."],
];

/**
 * Appends whatever the wallet actually said.
 *
 * A friendly sentence that hides the cause is worse than no sentence: it reads
 * as a complete explanation while leaving nothing to act on or report.
 */
function withDetail(message: string, error: BaseError): string {
  const detail = error.details?.trim() || error.shortMessage?.trim();
  return detail ? `${message}\n\n(${detail})` : message;
}

export function describeTxError(error: unknown): string {
  // The formatted message is for the user; the full error, with its cause
  // chain, is for whoever has the devtools open.
  if (error instanceof Error) console.error("transaction failed:", error);

  if (error instanceof BaseError) {
    if (error.walk((e) => e instanceof UserRejectedRequestError)) {
      return "Transaksi dibatalkan di wallet.";
    }
    // -32002. Usually a request the wallet is still holding — a popup behind
    // the window, or a second click while the first was open. But the wallet
    // also uses this code when it considers the network itself unavailable,
    // and the two need opposite fixes. So the underlying text is appended
    // rather than swallowed: without it there is no way to tell which one
    // this is.
    if (error.walk((e) => e instanceof ResourceUnavailableRpcError)) {
      return withDetail(
        "Wallet menolak permintaan ini. Biasanya karena masih ada permintaan " +
          "tertunda di MetaMask — buka ekstensinya, selesaikan atau tolak, lalu " +
          "coba lagi. Kalau tidak ada yang tertunda, MetaMask menganggap jaringan " +
          "ini sedang tidak tersedia; hapus lalu tambahkan ulang jaringannya.",
        error,
      );
    }
    // The RPC never answered, or answered 5xx. Worth its own message because
    // the default — "The request took too long to respond." — reads like the
    // transaction might be somewhere in flight. It is not: this fires while
    // simulating, before the wallet is ever asked to sign.
    if (
      error.walk((e) => e instanceof TimeoutError) ||
      error.walk((e) => e instanceof HttpRequestError)
    ) {
      return withDetail(
        "Jaringan RPC sedang tidak merespons, jadi transaksi belum sempat " +
          "dibuat — tidak ada dana yang berpindah. Tunggu sebentar lalu coba " +
          "lagi; kalau terus berulang, laporkan ke kanal support resmi.",
        error,
      );
    }
    if (error.walk((e) => e instanceof ChainMismatchError)) {
      return "Wallet sedang berada di jaringan lain. Pindah jaringan lalu coba lagi.";
    }
  }

  const raw = error instanceof Error ? error.message : String(error);

  // Older wallets surface a rejection as a plain string rather than a typed
  // error, so the text check stays as a second line of defence.
  if (raw.includes("User rejected") || raw.includes("User denied")) {
    return "Transaksi dibatalkan di wallet.";
  }

  for (const [name, message] of CONTRACT_ERRORS) {
    if (raw.includes(name)) return message;
  }

  if (error instanceof BaseError) {
    return [error.shortMessage, error.details].filter(Boolean).join(" ").trim();
  }
  return raw.split("\n")[0]!;
}
